import React, { useState, useEffect } from 'react';
import { Container, Spinner, Alert, Button, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import api from '../services/api';
import ConversationTable from '../components/conversations/ConversationTable';
import EmptyState from '../components/common/EmptyState';
import Footer from '../components/Footer';
import './ChatHistory.css';

interface Conversation {
  id: string;
  title?: string;
  lastMessage?: string;
  createdAt: Date;
  updatedAt: Date;
  messageCount?: number;
  artifactCount?: number;
  projectId?: string;
}

const ChatHistory: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const { currentUser } = useAuth();
  const { projects } = useProjects();
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchConversations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchConversations = async (page: number) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const response = await api.get<{ conversations: Conversation[], hasMore: boolean }>(`/api/chat/conversations?limit=${perPage}&page=${page}`);

      // Deduplicate conversations by ID
      const uniqueConversations = (response.conversations || response as any).reduce((acc: Conversation[], conv: Conversation) => {
        if (!acc.find(c => c.id === conv.id)) {
          acc.push(conv);
        }
        return acc;
      }, [] as Conversation[]);

      setConversations(uniqueConversations);
      setHasMore(response.hasMore || false);
      setCurrentPage(page);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = async (conversationId: string, projectId: string | null) => {
    try {
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, projectId: projectId || undefined }
          : conv
      ));
    } catch (err) {
      console.error('Failed to update conversation project:', err);
    }
  };

  const handleNewChat = () => {
    // Pass selected project to home page if a specific project is selected
    if (selectedProjectId && selectedProjectId !== 'all') {
      navigate('/home', { state: { preselectedProjectId: selectedProjectId } });
    } else {
      navigate('/home');
    }
  };

  const handleProjectFilter = (projectId: string) => {
    setSelectedProjectId(projectId);
    setCurrentPage(1);
  };

  const handleDeleteConversation = (conversationId: string) => {
    // Remove the deleted conversation from local state
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
  };

  const handleUpdateConversation = (conversationId: string, title: string) => {
    // Update the conversation title in local state
    setConversations(prev => prev.map(conv =>
      conv.id === conversationId ? { ...conv, title } : conv
    ));
  };

  const filteredConversations = selectedProjectId === 'all'
    ? conversations
    : conversations.filter(conv => conv.projectId === selectedProjectId);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading conversations...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>conversations</h1>
          {filteredConversations.length > 0 && (
            <Button variant="primary" onClick={handleNewChat} className="new-conversation-btn">
              <Plus className="me-2" />
              Start New Conversation
            </Button>
          )}
        </div>

        <div className="mb-3">
          <Dropdown>
            <Dropdown.Toggle
              variant="link"
              id="project-filter-dropdown"
              style={{
                border: 'none',
                background: 'transparent',
                fontFamily: 'IBM Plex Mono, monospace',
                fontWeight: 600,
                fontSize: '12px',
                color: '#474920',
                textTransform: 'uppercase',
                textDecoration: 'none',
                padding: '0.375rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ChevronDown size={16} style={{ color: '#474920' }} />
              {selectedProjectId === 'all' ? 'All' : projects.find(p => p.id === selectedProjectId)?.name || 'All'}
            </Dropdown.Toggle>
            <Dropdown.Menu className="project-filter-dropdown-menu">
              <Dropdown.Item
                onClick={() => handleProjectFilter('all')}
                active={selectedProjectId === 'all'}
                className="project-filter-dropdown-menu-item"
              >
                All
              </Dropdown.Item>
              {projects.map(project => (
                <Dropdown.Item
                  key={project.id}
                  onClick={() => handleProjectFilter(project.id)}
                  active={selectedProjectId === project.id}
                  className="project-filter-dropdown-menu-item"
                >
                  {project.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {filteredConversations.length === 0 ? (
          <div className="d-flex justify-content-center mt-5">
            <EmptyState
              type={selectedProjectId === 'all' ? 'conversationsAll' : 'conversations'}
              onButtonClick={handleNewChat}
            />
          </div>
        ) : (
          <>
            <ConversationTable
              conversations={filteredConversations}
              onProjectChange={handleProjectChange}
              onDelete={handleDeleteConversation}
              onUpdate={handleUpdateConversation}
            />
            <div className="d-flex justify-content-end mt-3 mb-3">
              <div className="pagination-controls d-flex align-items-center gap-2">
                <button
                  className="pagination-btn"
                  onClick={() => fetchConversations(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                >
                  Previous
                </button>
                <span className="pagination-number active">{currentPage}</span>
                {hasMore && (
                  <span
                    className="pagination-number"
                    onClick={() => fetchConversations(currentPage + 1)}
                    style={{ cursor: 'pointer' }}
                  >
                    {currentPage + 1}
                  </span>
                )}
                <button
                  className="pagination-btn"
                  onClick={() => fetchConversations(currentPage + 1)}
                  disabled={!hasMore || loading}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default ChatHistory;